import { Loader2 } from "lucide-react";
import ScrollArea from "../common/ScrollArea";

export default function TeamsSidebar({
  teams,
  selectedTeamId,
  teamAccentMap,
  isLoading,
  onTeamSelect,
}) {
  const getTeamMemberCount = (team) =>
    team.totalMembers ?? team.memberIds?.length ?? 0;

  return (
    <>
      {/* ── Desktop sidebar ── */}
      <div className="hidden md:flex w-56 shrink-0 border-r border-(--color-border) bg-transparent flex-col h-full overflow-hidden">
        <div className="px-4 pt-4 pb-2 shrink-0">
          <p className="text-[9px] font-black uppercase text-(--color-text-muted)">
            All Teams
          </p>
        </div>
        <ScrollArea as="nav" className="flex-1 py-1 px-2 space-y-0.5 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 gap-2 text-(--color-text-muted)">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : teams.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <p className="text-xs text-(--color-text-muted)">No teams yet</p>
            </div>
          ) : (
            teams.map((team) => {
              const accent = teamAccentMap[team.id];
              const isActive = team.id === selectedTeamId;
              const memberCount = getTeamMemberCount(team);
              return (
                <button
                  key={team.id}
                  onClick={() => onTeamSelect(team.id)}
                  className="w-full text-left px-3 py-3 rounded-lg flex items-center gap-3 transition-all bg-(--color-bg)/50 hover:bg-(--color-bg)/50"
                  style={{
                    background: isActive ? `${accent}18` : "transparent",
                    border: `1px solid ${isActive ? `${accent}40` : "transparent"}`,
                    transition: "background 0.2s ease, border-color 0.2s ease",
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-black shrink-0"
                    style={{
                      background: `${accent}${isActive ? "22" : "12"}`,
                      color: accent,
                      border: `1.5px solid ${accent}${isActive ? "45" : "22"}`,
                    }}
                  >
                    {team.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate text-(--color-text-primary)">
                      {team.name}
                    </div>
                    <div className="text-[10px] text-(--color-text-muted) mt-0.5">
                      {memberCount} member{memberCount !== 1 ? "s" : ""}
                    </div>
                  </div>
                  {isActive && (
                    <div
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: accent }}
                    />
                  )}
                </button>
              );
            })
          )}
        </ScrollArea>
      </div>

      {/* ── Mobile: full list (shown when no team selected) ── */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-(--color-text-muted)">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading teams…</span>
          </div>
        ) : teams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-2 text-(--color-text-muted)">
            <p className="text-sm">No teams yet</p>
          </div>
        ) : (
          <ScrollArea className="flex-1 px-4 py-3 space-y-2">
            {teams.map((team) => {
              const accent = teamAccentMap[team.id];
              const memberCount = getTeamMemberCount(team);
              return (
                <button
                  key={team.id}
                  onClick={() => onTeamSelect(team.id)}
                  className="w-full text-left px-4 py-3.5 rounded-xl flex items-center gap-3 transition-all bg-(--color-bg)/50 hover:bg-transparent"
                  style={{
                    border: `1px solid var(--color-border)`,
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black shrink-0"
                    style={{
                      background: `${accent}18`,
                      color: accent,
                      border: `1.5px solid ${accent}35`,
                    }}
                  >
                    {team.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-(--color-text-primary) truncate">
                      {team.name}
                    </div>
                    <div className="text-xs text-(--color-text-muted) mt-0.5">
                      {memberCount} member{memberCount !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <svg
                    className="w-4 h-4 text-(--color-text-muted) shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              );
            })}
          </ScrollArea>
        )}
      </div>
    </>
  );
}